import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Pencil, Trash2, X, Download, Send, Filter, ArrowUpDown, ArrowUp, ArrowDown, Star, CalendarDays, CalendarPlus } from "lucide-react";
import { DevisDetailModal } from "./DevisDetailModal";
import { CreateDevisModal } from "./CreateDevisModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = d => d ? d.split('-').reverse().join('/') : '—';


const STATUTS = ["devis", "réservé", "en_attente", "annulé"];
const STATUT_BADGE = {
  "devis": "bg-amber-100 text-amber-800",
  "réservé": "bg-green-100 text-green-800",
  "annulé": "bg-red-100 text-red-700",
  "en_attente": "bg-orange-100 text-orange-800",
};
const STATUT_LABEL = { "devis": "Devis", "réservé": "Réservé", "annulé": "Annulé", "en_attente": "En attente" };

export default function AdminDevis() {
  const { authHeaders, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState("all");
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [themes, setThemes] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [lieuxCeremonie, setLieuxCeremonie] = useState([]);
  const [salles, setSalles] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [sortBy, setSortBy] = useState("date_mariage");
  const [sortDir, setSortDir] = useState("asc");
  const [hidePast, setHidePast] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const fetchDevis = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (statut !== "all") params.append("statut", statut);
    if (showArchived) params.append("include_archived", "true");
    axios.get(`${API}/devis?${params}`, { headers: authHeaders })
      .then(r => setDevis(r.data)).finally(() => setLoading(false));
  }, [search, statut, showArchived, authHeaders]);

  const fetchThemes = useCallback(() => { axios.get(`${API}/themes`).then(r => setThemes(r.data)); }, []);
  const fetchLieux = useCallback(() => { axios.get(`${API}/lieux`).then(r => setLieux(r.data)); }, []);
  const fetchLieuxCeremonie = useCallback(() => { axios.get(`${API}/lieux-ceremonie`).then(r => setLieuxCeremonie(r.data)); }, []);
  const fetchSalles = useCallback(() => { axios.get(`${API}/salles`).then(r => setSalles(r.data)); }, []);
  const fetchClients = useCallback(() => { axios.get(`${API}/clients`, { headers: authHeaders }).then(r => setClients(r.data)); }, [authHeaders]);
  const fetchContacts = useCallback(() => { axios.get(`${API}/contacts`, { headers: authHeaders }).then(r => setContacts(r.data)); }, [authHeaders]);

  useEffect(() => { fetchDevis(); }, [fetchDevis]);
  useEffect(() => {
    axios.get(`${API}/vehicles`).then(r => setVehicles(r.data));
    fetchThemes(); fetchLieux(); fetchLieuxCeremonie(); fetchSalles(); fetchClients(); fetchContacts();
  }, [fetchThemes, fetchLieux, fetchLieuxCeremonie, fetchSalles, fetchClients, fetchContacts]);

  const downloadPdf = async (d) => {
    const res = await axios.get(`${API}/devis/${d.id}/pdf`, { headers: authHeaders, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `devis-${d.numero}.pdf`; a.click();
  };

  const downloadIcs = async (d) => {
    try {
      const res = await axios.get(`${API}/devis/${d.id}/ics`, { headers: authHeaders, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mariage-${(d.client_name || "").replace(/[^A-Za-z0-9_-]+/g, "-")}-${d.date_mariage}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { alert("Impossible de générer l'événement calendrier"); }
  };

  const sendDevis = async (d) => {
    try {
      const res = await axios.post(`${API}/devis/${d.id}/send`, {}, { headers: authHeaders });
      if (res.data.simulated) {
        alert(`⚠️ Email simulé (SendGrid non configuré)\n\nLe devis serait envoyé à ${d.client_email}`);
      } else {
        alert(`✓ Devis envoyé à ${d.client_email} !`);
      }
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || "Impossible d'envoyer l'email"}`);
    }
  };

  const deleteDevis = async (id) => {
    await axios.delete(`${API}/devis/${id}`, { headers: authHeaders });
    setDeleting(null);
    if (selectedDevis?.id === id) setSelectedDevis(null);
    fetchDevis();
  };

  const sendReview = async (d) => {
    try {
      await axios.post(`${API}/devis/${d.id}/send-review`, {}, { headers: authHeaders });
      alert(`✓ Demande d'avis envoyée à ${d.client_email} !`);
      fetchDevis();
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || "Impossible d'envoyer la demande d'avis"}`);
    }
  };

  const sendMonthlyRecap = async () => {
    try {
      await axios.post(`${API}/notifications/send-monthly-recap`, {}, { headers: authHeaders });
      alert("✓ Récap mensuel envoyé aux chauffeurs et décoratrices !");
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || "Impossible d'envoyer le récap"}`);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const filteredDevis = hidePast ? devis.filter(d => (d.date_mariage || "") >= today) : devis;

  const sortedDevis = [...filteredDevis].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (sortBy === "montant" || sortBy === "total_regle" || sortBy === "reste_solder") {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else if (sortBy === "date_mariage" || sortBy === "created_at") {
      aVal = aVal || "";
      bVal = bVal || "";
    } else {
      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }
    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown size={12} className="opacity-30" />;
    return sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="devis-title">{isAdmin ? "Devis & Réservations" : "Mes Réservations"}</h1>
          <p className="text-gray-500 text-sm mt-1">{isAdmin ? "Gérez vos demandes de devis et réservations" : "Consultez et gérez vos locations"}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={sendMonthlyRecap} title="Envoyer le récap mensuel aux chauffeurs et décoratrices"
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors rounded">
              <CalendarDays size={15} /> Récap mensuel
            </button>
          )}
          <button onClick={() => setShowCreate(true)} data-testid="create-devis-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors rounded">
            <Plus size={16} /> CRÉER UN DEVIS
          </button>
        </div>
      </div>


      <div className="bg-white p-4 shadow-sm mb-4 flex flex-col sm:flex-row gap-3 items-center">
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, n° devis..."
            data-testid="devis-search"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843]" />
        </div>
        <div className="relative">
          <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select value={statut} onChange={e => setStatut(e.target.value)} data-testid="statut-filter"
            className="pl-8 pr-8 py-2.5 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843] bg-white appearance-none min-w-[160px]">
            <option value="all">Tous les statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABEL[s]}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap" data-testid="hide-past-toggle">
          <input type="checkbox" checked={hidePast} onChange={e => setHidePast(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-[#1B2E4B] focus:ring-[#D4A843] cursor-pointer" />
          <span className="text-sm text-gray-600">Masquer les passés</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap" data-testid="show-archives-toggle">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-[#1B2E4B] focus:ring-[#D4A843] cursor-pointer" />
          <span className="text-sm text-gray-600">Afficher les archives</span>
        </label>
      </div>

      {/* ── Mobile card view ───────────────────────────────────────── */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <p className="text-center py-10 text-gray-400">Chargement...</p>
        ) : sortedDevis.length === 0 ? (
          <p className="text-center py-10 text-gray-400">Aucun devis trouvé</p>
        ) : sortedDevis.map(d => {
          const workflowStep = d.workflow_step || 0;
          const workflowStatus = d.workflow_status || "active";
          const workflowResponse = d.workflow_response;
          let relanceLabel = "";
          let relanceClass = "bg-gray-100 text-gray-500";
          if (d.statut === "réservé") { relanceLabel = "Réservé"; relanceClass = "bg-green-100 text-green-700"; }
          else if (d.statut === "annulé") { relanceLabel = "Annulé"; relanceClass = "bg-gray-100 text-gray-400"; }
          else if (workflowResponse === "not_interested") { relanceLabel = "Décliné"; relanceClass = "bg-red-100 text-red-600"; }
          else if (workflowStatus === "stopped") { relanceLabel = "Arrêté"; relanceClass = "bg-gray-100 text-gray-500"; }
          else if (workflowStatus === "completed") { relanceLabel = "Terminé"; relanceClass = "bg-blue-100 text-blue-600"; }
          else if (workflowStep === 0) { relanceLabel = "Initial"; relanceClass = "bg-yellow-100 text-yellow-700"; }
          else { relanceLabel = `J+${[3,7,14,21][workflowStep-1] || "?"}`; relanceClass = "bg-orange-100 text-orange-600"; }
          return (
            <div key={d.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedDevis(d)} className="text-[#D4A843] font-bold text-base">{d.numero}</button>
                  {d.archived && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-600">Archivé</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${STATUT_BADGE[d.statut] || "bg-gray-100 text-gray-600"}`}>
                    {STATUT_LABEL[d.statut] || d.statut}
                  </span>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${relanceClass}`}>{relanceLabel}</span>
                </div>
              </div>
              {/* Client + Véhicule */}
              <div className="mb-3">
                <p className="font-semibold text-gray-900">{d.client_name}</p>
                <p className="text-xs text-gray-400">{d.client_ville}</p>
                <p className="text-sm text-gray-600 mt-1">{d.vehicle_name}</p>
                <p className="text-sm text-gray-500">{fmt(d.date_mariage)}</p>
              </div>
              {/* Montants */}
              <div className="flex gap-4 py-2 border-t border-b border-gray-100 mb-3">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Montant</p>
                  <p className="font-bold text-gray-900">{d.montant}€</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Réglé</p>
                  <p className="font-bold text-green-600">{(d.total_regle || 0) > 0 ? `${d.total_regle}€` : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Reste</p>
                  {(d.reste_solder || d.montant) > 0
                    ? <p className="font-bold text-orange-500">{d.reste_solder ?? d.montant}€</p>
                    : <p className="font-bold text-green-600 text-xs">Soldé</p>}
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedDevis(d)} className="text-gray-400 hover:text-gray-700"><Pencil size={16} /></button>
                <button onClick={() => downloadPdf(d)} className="text-gray-400 hover:text-blue-600"><Download size={16} /></button>
                <button onClick={() => sendDevis(d)} className="text-gray-400 hover:text-green-600"><Send size={16} /></button>
                {(d.date_mariage || "") < today && (
                  d.review_email_sent
                    ? <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">Avis ✓</span>
                    : <button onClick={() => sendReview(d)} className="text-gray-400 hover:text-yellow-500"><Star size={16} /></button>
                )}
                <button onClick={() => setDeleting(d.id)} className="text-gray-400 hover:text-red-500 ml-auto"><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Desktop table view ───────────────────────────────────────── */}
      <div className="hidden md:block bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="devis-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase bg-gray-50">
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("numero")}>
                  <span className="flex items-center gap-1">N° Devis <SortIcon column="numero" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("client_name")}>
                  <span className="flex items-center gap-1">Client <SortIcon column="client_name" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("vehicle_name")}>
                  <span className="flex items-center gap-1">Véhicule <SortIcon column="vehicle_name" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("date_mariage")}>
                  <span className="flex items-center gap-1">Date Mariage <SortIcon column="date_mariage" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("montant")}>
                  <span className="flex items-center gap-1">Montant <SortIcon column="montant" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("total_regle")}>
                  <span className="flex items-center gap-1">Réglé <SortIcon column="total_regle" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("reste_solder")}>
                  <span className="flex items-center gap-1">Reste <SortIcon column="reste_solder" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap cursor-pointer hover:text-gray-600" onClick={() => handleSort("statut")}>
                  <span className="flex items-center gap-1">Statut <SortIcon column="statut" /></span>
                </th>
                <th className="px-4 py-4 text-left whitespace-nowrap">Relance</th>
                <th className="px-4 py-4 text-left whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">Chargement...</td></tr>
              ) : sortedDevis.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">Aucun devis trouvé</td></tr>
              ) : sortedDevis.map(d => {
                const workflowStatus = d.workflow_status || "active";
                const workflowStep = d.workflow_step || 0;
                const workflowResponse = d.workflow_response;
                let relanceLabel = "";
                let relanceClass = "bg-gray-100 text-gray-500";
                if (d.statut === "réservé") { relanceLabel = "Réservé"; relanceClass = "bg-green-100 text-green-700"; }
                else if (d.statut === "annulé") { relanceLabel = "Annulé"; relanceClass = "bg-gray-100 text-gray-400"; }
                else if (workflowResponse === "not_interested") { relanceLabel = "Décliné"; relanceClass = "bg-red-100 text-red-600"; }
                else if (workflowStatus === "stopped") { relanceLabel = "Arrêté"; relanceClass = "bg-gray-100 text-gray-500"; }
                else if (workflowStatus === "completed") { relanceLabel = "Terminé"; relanceClass = "bg-blue-100 text-blue-600"; }
                else if (workflowStep === 0) { relanceLabel = "Initial"; relanceClass = "bg-yellow-100 text-yellow-700"; }
                else { relanceLabel = `J+${[3,7,14,21][workflowStep-1] || "?"}`; relanceClass = "bg-orange-100 text-orange-600"; }

                return (
                  <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedDevis(d)} data-testid={`devis-row-${d.numero}`}
                        className="text-[#D4A843] font-medium hover:underline">{d.numero}</button>
                      {d.archived && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-600">Archivé</span>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{d.client_name}</p>
                      <p className="text-xs text-gray-400">{d.client_ville}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{d.vehicle_name}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(d.date_mariage)}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{d.montant}€</td>
                    <td className="px-4 py-3">
                      {(d.total_regle || 0) > 0
                        ? <span className="font-semibold text-green-600">{d.total_regle}€</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {(d.reste_solder || d.montant) > 0
                        ? <span className="font-semibold text-orange-500">{d.reste_solder ?? d.montant}€</span>
                        : <span className="text-green-600 font-semibold text-xs">Soldé</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${STATUT_BADGE[d.statut] || "bg-gray-100 text-gray-600"}`}>
                        {STATUT_LABEL[d.statut] || d.statut}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${relanceClass}`} data-testid={`relance-${d.id}`}>
                        {relanceLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setSelectedDevis(d)} title="Voir / Modifier" data-testid={`edit-${d.id}`}
                          className="text-gray-400 hover:text-gray-700 transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => downloadPdf(d)} title="Devis PDF" data-testid={`pdf-${d.id}`}
                          className="text-gray-400 hover:text-blue-600 transition-colors"><Download size={14} /></button>
                        <button onClick={() => sendDevis(d)} title="Envoyer devis" data-testid={`send-${d.id}`}
                          className="text-gray-400 hover:text-green-600 transition-colors"><Send size={14} /></button>
                        <button onClick={() => downloadIcs(d)} title="Ajouter à mon calendrier" data-testid={`ics-${d.id}`}
                          className="text-gray-400 hover:text-[#D4A843] transition-colors"><CalendarPlus size={14} /></button>
                        {(d.date_mariage || "") < today && (
                          d.review_email_sent
                            ? <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded whitespace-nowrap">Avis ✓</span>
                            : <button onClick={() => sendReview(d)} title="Demande d'avis" data-testid={`review-${d.id}`}
                                className="text-gray-400 hover:text-yellow-500 transition-colors"><Star size={14} /></button>
                        )}
                        <button onClick={() => setDeleting(d.id)} title="Supprimer" data-testid={`delete-${d.id}`}
                          className="text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedDevis && (
        <DevisDetailModal
          devis={selectedDevis}
          vehicles={vehicles}
          themes={themes}
          lieux={lieux}
          lieuxCeremonie={lieuxCeremonie}
          salles={salles}
          clients={clients}
          contacts={contacts}
          authHeaders={authHeaders}
          onClose={() => setSelectedDevis(null)}
          onUpdated={() => { fetchDevis(); fetchThemes(); fetchLieux(); fetchLieuxCeremonie(); fetchSalles(); }}
          onRefresh={{ fetchThemes, fetchLieux, fetchLieuxCeremonie, fetchSalles, fetchClients }}
        />
      )}

      {showCreate && (
        <CreateDevisModal
          vehicles={vehicles} themes={themes} lieux={lieux} lieuxCeremonie={lieuxCeremonie} salles={salles}
          clients={clients} contacts={contacts} authHeaders={authHeaders}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchDevis(); }}
          onRefresh={{ fetchThemes, fetchLieux, fetchLieuxCeremonie, fetchSalles, fetchClients }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 mb-3">Supprimer le devis ?</h3>
            <p className="text-gray-500 text-sm mb-6">Les factures et règlements associés seront aussi supprimés.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-5 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
              <button onClick={() => deleteDevis(deleting)} data-testid="confirm-delete-btn"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
